/**
 * Parts tree editing for model documents: multi-part delete / duplicate,
 * drag-and-drop reparent and reorder, group into a new empty, hide / lock /
 * isolate, rename and the search filter of the Parts panel.
 *
 * Everything is pure (a new document out, the input untouched) so each tree
 * action is one `useModel.update(doc)` call, i.e. one undo step. Parts are
 * named by prism part paths (`body/arm#2`) at the API surface; internally the
 * work is done on index paths and the results are converted back, because a
 * move can rename a part (`arm` → `arm#2` next to another `arm`).
 */

import {
  partAt,
  partPathOf,
  partRows,
  resolvePartPath,
  updatePart,
  type IndexPath,
  type ModelDoc,
  type ModelPart,
  type PartRow,
} from './model-doc.js';
import type { TransformDesc } from './scene-doc.js';
import { composeMatrix, decomposeMatrix, IDENTITY_MAT, invertAffine, multiply, type Mat4 } from './transform-math.js';

/** Where a dragged part lands relative to the row under the pointer. */
export type DropPosition = 'before' | 'after' | 'into';

/** A Parts panel row; `match` marks rows that matched the search (the rest are their ancestors). */
export interface TreeRow extends PartRow {
  match: boolean;
}

/* ------------------------------------------------------------------ */
/* Selection                                                           */

/**
 * Every selected part: the multi-selection when it still contains the active
 * part, else just the active part (the viewport can change `selected` alone).
 */
export function effectiveSelection(selected: string | null, multi: readonly string[]): string[] {
  if (!selected) return [];
  return multi.includes(selected) ? [...multi] : [selected];
}

/** Shift+click: the visible rows between the anchor and the clicked row (inclusive). */
export function rangeSelect(rows: readonly { partPath: string }[], anchor: string | null, target: string): string[] {
  const paths = rows.map((r) => r.partPath);
  const b = paths.indexOf(target);
  const a = anchor ? paths.indexOf(anchor) : -1;
  if (b < 0) return [target];
  if (a < 0) return [target];
  return paths.slice(Math.min(a, b), Math.max(a, b) + 1);
}

/** Every part path in tree order. */
export function allPartPaths(doc: ModelDoc): string[] {
  return partRows(doc).map((r) => r.partPath);
}

/** All descendants of a part (children, grandchildren...) in tree order. */
export function descendantPartPaths(doc: ModelDoc, partPath: string): string[] {
  const prefix = `${partPath}/`;
  return allPartPaths(doc).filter((p) => p.startsWith(prefix));
}

/* ------------------------------------------------------------------ */
/* Index path helpers                                                  */

/** Tree (document) order: parents before children, siblings by index. */
export function compareIndexPaths(a: IndexPath, b: IndexPath): number {
  for (let i = 0; i < Math.min(a.length, b.length); i += 1) {
    if (a[i] !== b[i]) return a[i]! - b[i]!;
  }
  return a.length - b.length;
}

/** True when `a` is `b` or one of its ancestors. */
export function isAncestorOrSelf(a: IndexPath, b: IndexPath): boolean {
  return a.length <= b.length && a.every((v, i) => b[i] === v);
}

/** Resolve part paths, drop unknown ones and duplicates, sort in tree order. */
function resolveAll(doc: ModelDoc, partPaths: readonly string[]): number[][] {
  const seen = new Set<string>();
  const out: number[][] = [];
  for (const p of partPaths) {
    const index = resolvePartPath(doc, p);
    if (!index || seen.has(index.join('/'))) continue;
    seen.add(index.join('/'));
    out.push(index);
  }
  return out.sort(compareIndexPaths);
}

/** Drop paths whose ancestor is also listed (moving a parent moves its children). */
function rootsOf(paths: readonly number[][]): number[][] {
  return paths.filter((p) => !paths.some((q) => q !== p && q.length < p.length && isAncestorOrSelf(q, p)));
}

/** Selected parts without their selected descendants, as part paths in tree order. */
export function selectionRoots(doc: ModelDoc, partPaths: readonly string[]): string[] {
  return rootsOf(resolveAll(doc, partPaths)).map((p) => partPathOf(doc, p));
}

/** Copy-on-write edit of the child list under `parent` ([] = the root list); empty lists are removed. */
function editChildren(doc: ModelDoc, parent: IndexPath, edit: (list: ModelPart[]) => ModelPart[]): ModelDoc {
  if (parent.length === 0) return { ...doc, parts: edit(doc.parts.slice()) };
  return updatePart(doc, parent, (part) => {
    const children = edit((part.children ?? []).slice());
    const next: ModelPart = { ...part, children };
    if (children.length === 0) delete next.children;
    return next;
  });
}

/** Index paths of the given part objects (by identity), in tree order. */
function findParts(doc: ModelDoc, wanted: ReadonlySet<ModelPart>): number[][] {
  const out: number[][] = [];
  const walk = (list: readonly ModelPart[], prefix: number[]) => {
    list.forEach((part, i) => {
      const path = [...prefix, i];
      if (wanted.has(part)) out.push(path);
      if (part.children) walk(part.children, path);
    });
  };
  walk(doc.parts, []);
  return out;
}

/** Product of the part transforms from the root down to `path` (the root list = identity). */
function worldOf(doc: ModelDoc, path: IndexPath): Mat4 {
  let m = IDENTITY_MAT.slice();
  for (let i = 1; i <= path.length; i += 1) {
    m = multiply(m, composeMatrix(partAt(doc, path.slice(0, i))?.transform));
  }
  return m;
}

function sameMatrix(a: Mat4, b: Mat4): boolean {
  return a.every((v, i) => Math.abs(v - b[i]!) < 1e-6);
}

/** A transform without its identity components (the document omits defaults). */
function tidyTransform(t: Required<TransformDesc>): TransformDesc | undefined {
  const out: TransformDesc = {};
  if (t.position.some((v) => v !== 0)) out.position = t.position;
  if (t.rotation.some((v) => v !== 0)) out.rotation = t.rotation;
  if (t.scale.some((v) => v !== 1)) out.scale = t.scale;
  return Object.keys(out).length ? out : undefined;
}

/* ------------------------------------------------------------------ */
/* Structural edits                                                    */

/** Delete parts (and their children). One document change for the whole selection. */
export function deleteParts(doc: ModelDoc, partPaths: readonly string[]): ModelDoc {
  const roots = rootsOf(resolveAll(doc, partPaths));
  let next = doc;
  // Last first, so earlier index paths stay valid.
  for (const path of [...roots].reverse()) {
    const index = path[path.length - 1]!;
    next = editChildren(next, path.slice(0, -1), (list) => list.filter((_, i) => i !== index));
  }
  return next;
}

/** Copy each part (with children) right after itself. Returns the copies' index paths. */
export function duplicateParts(doc: ModelDoc, partPaths: readonly string[]): { doc: ModelDoc; paths: number[][] } {
  const roots = rootsOf(resolveAll(doc, partPaths));
  const copies = new Set<ModelPart>();
  let next = doc;
  for (const path of [...roots].reverse()) {
    const part = partAt(next, path);
    if (!part) continue;
    const copy = JSON.parse(JSON.stringify(part)) as ModelPart;
    copies.add(copy);
    const index = path[path.length - 1]!;
    next = editChildren(next, path.slice(0, -1), (list) => {
      list.splice(index + 1, 0, copy);
      return list;
    });
  }
  return { doc: next, paths: findParts(next, copies) };
}

/** True when the parts can be dropped there (never into themselves or their own descendants). */
export function canDropParts(doc: ModelDoc, partPaths: readonly string[], target: string | null, position: DropPosition): boolean {
  const roots = rootsOf(resolveAll(doc, partPaths));
  if (!roots.length) return false;
  if (target === null) return true;
  const t = resolvePartPath(doc, target);
  if (!t) return false;
  if (roots.some((r) => isAncestorOrSelf(r, t))) return false;
  return position === 'into' || t.length > 0;
}

/**
 * Move parts to a new place in the tree: before / after a sibling row, into a
 * part (as its last children), or with `target` null to the end of the root
 * list. With `keepWorld` each moved part keeps its world placement (its local
 * transform is re-expressed in the new parent's space); otherwise the local
 * transform is kept as is. Returns null when the drop is not allowed (into
 * itself or its own descendants) or nothing would move.
 */
export function moveParts(
  doc: ModelDoc,
  partPaths: readonly string[],
  target: string | null,
  position: DropPosition,
  keepWorld = false,
): { doc: ModelDoc; paths: number[][] } | null {
  const roots = rootsOf(resolveAll(doc, partPaths));
  const t = target === null ? null : resolvePartPath(doc, target);
  if (target !== null && !t) return null;
  return moveIndexPaths(doc, roots, t, position, keepWorld);
}

function moveIndexPaths(
  doc: ModelDoc,
  roots: readonly number[][],
  target: number[] | null,
  position: DropPosition,
  keepWorld: boolean,
): { doc: ModelDoc; paths: number[][] } | null {
  if (!roots.length) return null;
  if (target && roots.some((r) => isAncestorOrSelf(r, target))) return null;
  // Parent the parts end up under, in the original document.
  const newParent = target === null ? [] : position === 'into' ? target : target.slice(0, -1);
  const parentWorld = worldOf(doc, newParent);
  const moved = roots.map((path) => {
    const part = partAt(doc, path)!;
    if (!keepWorld) return part;
    const oldParentWorld = worldOf(doc, path.slice(0, -1));
    if (sameMatrix(oldParentWorld, parentWorld)) return part;
    const world = multiply(oldParentWorld, composeMatrix(part.transform));
    const local = tidyTransform(decomposeMatrix(multiply(invertAffine(parentWorld), world)));
    const next: ModelPart = { ...part };
    if (local) next.transform = local;
    else delete next.transform;
    return next;
  });

  // Where the target sits once the moved parts are taken out.
  const adjusted = target ? target.slice() : null;
  if (adjusted) {
    for (const r of roots) {
      const k = r.length - 1;
      if (k < adjusted.length && r.slice(0, k).every((v, i) => target![i] === v) && r[k]! < target![k]!) adjusted[k] = adjusted[k]! - 1;
    }
  }

  let next = doc;
  for (const path of [...roots].reverse()) {
    const index = path[path.length - 1]!;
    next = editChildren(next, path.slice(0, -1), (list) => list.filter((_, i) => i !== index));
  }

  let parent: number[];
  let at: number;
  if (!adjusted) {
    parent = [];
    at = next.parts.length;
  } else if (position === 'into') {
    parent = adjusted;
    at = partAt(next, adjusted)?.children?.length ?? 0;
  } else {
    parent = adjusted.slice(0, -1);
    at = adjusted[adjusted.length - 1]! + (position === 'after' ? 1 : 0);
  }
  next = editChildren(next, parent, (list) => {
    list.splice(at, 0, ...moved);
    return list;
  });
  const paths = moved.map((_, i) => [...parent, at + i]);
  return { doc: next, paths };
}

/**
 * Put the parts under a new empty part (Blender: Parent to new empty). The
 * empty is created where the first part was, in its parent; every part keeps
 * its world placement. Returns the empty's index path.
 */
export function groupParts(doc: ModelDoc, partPaths: readonly string[], name = 'group'): { doc: ModelDoc; group: number[]; paths: number[][] } | null {
  const roots = rootsOf(resolveAll(doc, partPaths));
  const first = roots[0];
  if (!first) return null;
  const parent = first.slice(0, -1);
  const at = first[first.length - 1]!;
  const empty: ModelPart = { name, shape: { type: 'empty' } };
  const withEmpty = editChildren(doc, parent, (list) => {
    list.splice(at, 0, empty);
    return list;
  });
  // Paths after the insertion: later siblings (and their subtrees) moved down by one.
  const shifted = roots.map((r) => {
    const out = r.slice();
    if (r.length > parent.length && parent.every((v, i) => r[i] === v) && r[parent.length]! >= at) out[parent.length] = out[parent.length]! + 1;
    return out;
  });
  const group = [...parent, at];
  const moved = moveIndexPaths(withEmpty, shifted, group, 'into', true);
  if (!moved) return null;
  return { doc: moved.doc, group, paths: moved.paths };
}

/** Move parts to the root list keeping their world placement (Blender: Clear Parent, keep transform). */
export function unparentParts(doc: ModelDoc, partPaths: readonly string[], keepWorld = true): { doc: ModelDoc; paths: number[][] } | null {
  const roots = rootsOf(resolveAll(doc, partPaths)).filter((p) => p.length > 1);
  return moveIndexPaths(doc, roots, null, 'into', keepWorld);
}

/** Make the other selected parts children of the active one (Blender: Set Parent To Object). */
export function parentToActive(doc: ModelDoc, partPaths: readonly string[], active: string, keepWorld = true): { doc: ModelDoc; paths: number[][] } | null {
  const others = partPaths.filter((p) => p !== active);
  if (!others.length) return null;
  return moveParts(doc, others, active, 'into', keepWorld);
}

/* ------------------------------------------------------------------ */
/* Flags, rename                                                       */

/** Set `hidden` / `locked` on several parts (false removes the field). */
export function setPartsFlag(doc: ModelDoc, partPaths: readonly string[], key: 'hidden' | 'locked', on: boolean): ModelDoc {
  let next = doc;
  for (const path of resolveAll(doc, partPaths)) {
    next = updatePart(next, path, (part) => {
      const out: ModelPart = { ...part };
      if (on) out[key] = true;
      else delete out[key];
      return out;
    });
  }
  return next;
}

function mapParts(list: readonly ModelPart[], prefix: number[], fn: (part: ModelPart, path: number[]) => ModelPart): ModelPart[] {
  return list.map((part, i) => {
    const path = [...prefix, i];
    const next = fn(part, path);
    return part.children ? { ...next, children: mapParts(part.children, path, fn) } : next;
  });
}

function withFlag(part: ModelPart, key: 'hidden' | 'locked', on: boolean): ModelPart {
  if (Boolean(part[key]) === on) return part;
  const out: ModelPart = { ...part };
  if (on) out[key] = true;
  else delete out[key];
  return out;
}

/**
 * Hide everything except the selection (Blender: Hide Unselected). Hiding a
 * part hides its children, so the selected parts' ancestors are shown and
 * their descendants are left alone.
 */
export function isolateParts(doc: ModelDoc, partPaths: readonly string[]): ModelDoc {
  const keep = resolveAll(doc, partPaths);
  if (!keep.length) return doc;
  return {
    ...doc,
    parts: mapParts(doc.parts, [], (part, path) => {
      if (keep.some((k) => isAncestorOrSelf(path, k))) return withFlag(part, 'hidden', false);
      if (keep.some((k) => isAncestorOrSelf(k, path))) return part;
      return withFlag(part, 'hidden', true);
    }),
  };
}

/** Show every hidden part (Blender: Reveal Hidden). */
export function revealAll(doc: ModelDoc): ModelDoc {
  return { ...doc, parts: mapParts(doc.parts, [], (part) => withFlag(part, 'hidden', false)) };
}

/** Rename a part ('' removes the name). Returns the part's new prism path. */
export function renamePart(doc: ModelDoc, partPath: string, name: string): { doc: ModelDoc; partPath: string } {
  const index = resolvePartPath(doc, partPath);
  if (!index) return { doc, partPath };
  const clean = name.trim();
  const oldName = partAt(doc, index)?.name ?? '';
  let next = updatePart(doc, index, (part) => {
    const out: ModelPart = { ...part };
    if (clean) out.name = clean;
    else delete out.name;
    return out;
  });
  const renamed = partPathOf(next, index);
  // Booleans that name the part (by name or path) follow it.
  if (renamed !== partPath) {
    const target = clean || renamed;
    next = repointBooleans(next, (w) => (w === partPath || (oldName !== '' && w === oldName) ? target : w));
  }
  return { doc: next, partPath: renamed };
}

/** Rewrites every boolean op's `with` through `map`. */
function repointBooleans(doc: ModelDoc, map: (target: string) => string): ModelDoc {
  let changed = false;
  const walk = (parts: readonly ModelPart[]): ModelPart[] =>
    parts.map((part) => {
      const ops = part.ops?.map((op) => {
        const w = (op as { with?: unknown }).with;
        if (op.op !== 'boolean' || typeof w !== 'string') return op;
        const next = map(w);
        if (next === w) return op;
        changed = true;
        return { ...op, with: next };
      });
      const children = part.children ? walk(part.children) : undefined;
      return { ...part, ...(ops ? { ops } : {}), ...(children ? { children } : {}) };
    });
  const parts = walk(doc.parts);
  return changed ? { ...doc, parts } : doc;
}

/** True when the part or one of its ancestors has `key` set (hidden / locked parent). */
export function inheritedFlag(doc: ModelDoc, partPath: string, key: 'hidden' | 'locked'): boolean {
  const index = resolvePartPath(doc, partPath);
  if (!index) return false;
  for (let i = 1; i <= index.length; i += 1) if (partAt(doc, index.slice(0, i))?.[key]) return true;
  return false;
}

/* ------------------------------------------------------------------ */
/* Rows                                                                */

/**
 * Rows of the Parts panel. Without a query the collapse state applies; with
 * one, every part whose name contains it (any case) is listed together with
 * its ancestors, whatever is collapsed.
 */
export function filterPartRows(doc: ModelDoc, query: string, collapsed: ReadonlySet<string> = new Set()): TreeRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return partRows(doc, collapsed).map((r) => ({ ...r, match: false }));
  const all = partRows(doc);
  const matches = all.filter((r) => (r.part.name ?? '').toLowerCase().includes(needle));
  const keep = new Set<string>();
  for (const r of matches) {
    const segments = r.partPath.split('/');
    for (let i = 1; i <= segments.length; i += 1) keep.add(segments.slice(0, i).join('/'));
  }
  const matched = new Set(matches.map((r) => r.partPath));
  return all.filter((r) => keep.has(r.partPath)).map((r) => ({ ...r, match: matched.has(r.partPath) }));
}

/** Convert index paths of a document to prism part paths. */
export function partPathsOf(doc: ModelDoc, paths: readonly IndexPath[]): string[] {
  return paths.map((p) => partPathOf(doc, p));
}
